import {
    UseMutationOptions,
    UseQueryOptions,
    useMutation,
    useQuery,
    useQueryClient
} from '@tanstack/react-query';
import OCGatewayClient from '../client/OCGatewayClient';
import { UserApiKey } from '../models/UserApiKey';
import { gatewayUserScopedQueryOptions } from './gatewayQueryUtils';

export const userApiKeysQueryKey = ['getUserApiKeys'] as const;

export interface CreatedUserApiKey {
    apiKey: UserApiKey;
    token: string;
}

export async function getUserApiKeys(): Promise<UserApiKey[]> {
    const response = await OCGatewayClient.request('getUserApiKeys', {}) as Array<Record<string, unknown>>;
    return response.map(apiKey => new UserApiKey(apiKey));
}

export async function createUserApiKey(name: string): Promise<CreatedUserApiKey> {
    const response = await OCGatewayClient.request('createUserApiKey', { name }) as {
        apiKey: Record<string, unknown>;
        token: string;
    };

    return {
        apiKey: new UserApiKey(response.apiKey),
        token: response.token
    };
}

export async function revokeUserApiKey(apiKeyId: number): Promise<void> {
    await OCGatewayClient.request('revokeUserApiKey', { apiKeyId });
}

export function useUserApiKeysQuery(options?: UseQueryOptions<UserApiKey[], unknown>) {
    return useQuery({
        queryKey: userApiKeysQueryKey,
        queryFn: getUserApiKeys,
        ...gatewayUserScopedQueryOptions,
        ...options
    });
}

export function useCreateUserApiKeyMutation(
    options?: UseMutationOptions<CreatedUserApiKey, unknown, string>
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createUserApiKey,
        ...options,
        onSuccess: (result, name, onMutateResult, mutationContext) => {
            queryClient.setQueryData<UserApiKey[]>(userApiKeysQueryKey, current => [
                result.apiKey,
                ...(current ?? []).filter(apiKey => apiKey.id !== result.apiKey.id)
            ]);
            options?.onSuccess?.(result, name, onMutateResult, mutationContext);
        }
    });
}

export function useRevokeUserApiKeyMutation(
    options?: UseMutationOptions<void, unknown, number>
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: revokeUserApiKey,
        ...options,
        onSuccess: (result, apiKeyId, onMutateResult, mutationContext) => {
            queryClient.setQueryData<UserApiKey[]>(userApiKeysQueryKey, current =>
                current?.filter(apiKey => apiKey.id !== apiKeyId) ?? []
            );
            options?.onSuccess?.(result, apiKeyId, onMutateResult, mutationContext);
        }
    });
}
